<!--
  --
  -- 時刻選択コンポーネント
  --
  -->
<template>
  <div ref="picker" class="c-time-picker">
    <div class="c-time-picker-spacer"/>
    <time
      v-for="time in times"
      :key="time.value"

      :class="time.classes"
      v-text="time.value"
      @click.prevent="onSelected(time)"
    />
    <div class="c-time-picker-spacer"/>
  </div>
</template>

<style lang="scss">
.c-time-picker {
  $item-height: 48px;
  $lines: 7;
  $picker-height: $lines * $item-height;
  $spacer-vert: ($picker-height - $item-height) / 2;

  height: $picker-height;
  overflow: scroll;
  -webkit-overflow-scrolling: touch;

  .c-time-picker-spacer {
    height: $spacer-vert;
  }

  time {
    display: block;
    font-size: 16px;
    letter-spacing: .1em;
    line-height: $item-height;
    text-align: center;
    cursor: pointer;
    user-select: none;

    &.c-time-picker-disabled {
      cursor: default;
    }

    &.c-time-picker-selected {
      font-size: 34px;
      font-weight: bold;
      letter-spacing: .05em;
    }
  }
}
</style>

<script>
import * as time from '@/libs/time'
import { tween } from '@/libs/animation'

export default {
  name: 'CTimePicker',

  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    /**
     * 選択値 hh:mm
     */
    value: {
      type: String
    },

    /**
     * 表示開始時刻
     */
    min: {
      type: String,
      default: '00:00'
    },

    /**
     * 表示終了時刻
     */
    max: {
      type: String,
      default: '24:00'
    },

    /**
     * 時間の間隔: デフォルト 30 分間隔
     */
    stepMinutes: {
      type: Number,
      default: 30
    },

    /**
     * 選択できる時刻を制限する
     */
    allowedTimes: {
      type: Function,
      default: value => true
    }
  },

  computed: {
    /**
     * 表示する時刻要素データ
     */
    times () {
      let first = t2m(this.min)
      const last = t2m(this.max)

      const t = []
      while (first <= last) {
        const value = m2t(first)
        const allowed = this.allowedTimes(value)
        let classes = []

        if (!allowed) {
          classes = ['c-time-picker-disabled', 'text--disabled']
        } else if (value === this.value) {
          classes = ['c-time-picker-selected', 'primary--text']
        }

        t.push({ value, allowed, classes })
        first += this.stepMinutes
      }

      return t
    }
  },

  mounted () {
    this.$nextTick(() => this.centering(false))
  },

  methods: {
    /**
     * アイテムが選択された
     */
    onSelected (item) {
      if (!item.allowed) {
        return
      }

      this.$emit('change', item.value)

      this.$nextTick(() => this.centering(true))
    },

    centering (animation = false) {
      const picker = this.$refs.picker
      if (!picker) {
        return
      }
      const selected = picker.querySelector('.c-time-picker-selected')
      if (!selected) {
        return
      }

      // 移動量を求める
      const from = picker.scrollTop
      const to = selected.offsetTop - picker.offsetTop - 148
      const delta = to - from
      if (delta === 0) {
        return
      }
      if (!animation) {
        // アニメーションなしでスクロール位置を設定する
        picker.scrollTop = to
      } else {
        // アニメーションを実行する
        tween(
          progress => {
            // progress に応じてスクロール位置を設定する
            picker.scrollTop = from + delta * progress
          },

          // 100ms
          100 * time.MILLI_SECOND
        )
      }
    }
  }
}

/**
 * hh:mm を 00:00 からの分数にする
 *
 * @param {String} 時刻
 * @returns {Number} 00:00 からの分数を返す
 */
function t2m (value) {
  const m = value.match(/^(?<h>\d{1,2}):(?<m>\d{1,2})$/)
  if (!m) {
    throw new Error(`t2m(${value}) Unhandled value`)
  }
  return m ? (m.groups.h - 0) * 60 + (m.groups.m - 0) : 0
}

/**
 * 00:00 からの分数を hh:mm 形式の文字列にする
 *
 * @param {Number} 分数
 * @returns {String} hh:mm 形式の文字列を返す
 */
function m2t (value) {
  const m = value % 60
  const h = (value - m) / 60
  return time.z2(h) + ':' + time.z2(m)
}
</script>
